import { PracticeTemplate, PracticeTemplateDetail } from '@cibo/core'
import { useAuth } from '@cibo/profile'
import { useSnackbar } from 'notistack'
import { equals } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import {
  useCreatePracticeTemplate,
  useDeletePracticeTemplate,
  usePracticeTemplates,
  useUpdatePracticeTemplate,
} from '../../../../../queries'

interface UseTemplateEditorStateParams {
  open: boolean
  onClose: () => void
  programId: string
  taskId: string
  initialTemplateId?: string
  allTraitIds: string[]
  createEntriesForTrait: (traitId: string) => PracticeTemplateDetail[]
}

export const useTemplateEditorState = ({
  open,
  onClose,
  programId,
  taskId,
  initialTemplateId,
  allTraitIds,
  createEntriesForTrait,
}: UseTemplateEditorStateParams) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { enqueueSnackbar } = useSnackbar()
  const { userId: authUserId, persona } = useAuth()
  const { userId: farmerUserId } = useProgramEngagementContext()

  const isAdvisorOrAdmin = persona === 'advisor' || persona === 'admin'

  const [selectedId, setSelectedId] = useState<string | 'new'>(initialTemplateId ?? 'new')
  const [draftName, setDraftName] = useState('')
  const [draftDetails, setDraftDetails] = useState<PracticeTemplateDetail[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [ownerScope, setOwnerScope] = useState<'farmer' | 'all'>('farmer')
  const [nameError, setNameError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showUnsavedGuard, setShowUnsavedGuard] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | 'new' | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const templateOwner = ownerScope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!

  // Refs so async callbacks always read the freshest values
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const draftNameRef = useRef(draftName)
  draftNameRef.current = draftName
  const selectedTemplateRef = useRef<PracticeTemplate | undefined>(undefined)
  const draftDetailsRef = useRef(draftDetails)
  draftDetailsRef.current = draftDetails
  const templateOwnerRef = useRef(templateOwner)
  templateOwnerRef.current = templateOwner
  const isCreatingRef = useRef(false)

  const ownTemplates = usePracticeTemplates(
    { programId, taskId, owner: authUserId },
    { enabled: open && !!programId && !isAdvisorOrAdmin }
  )
  const allTemplates = usePracticeTemplates(
    { programId, taskId },
    { enabled: open && !!programId && isAdvisorOrAdmin }
  )

  const createTemplate = useCreatePracticeTemplate()
  const updateTemplate = useUpdatePracticeTemplate()
  const deleteTemplate = useDeletePracticeTemplate()

  const sidebarTemplates: PracticeTemplate[] = useMemo(() => {
    if (isAdvisorOrAdmin && allTemplates.data) return allTemplates.data
    return ownTemplates.data ?? []
  }, [isAdvisorOrAdmin, allTemplates.data, ownTemplates.data])

  const isLoadingTemplates = isAdvisorOrAdmin ? allTemplates.isLoading : ownTemplates.isLoading

  const selectedTemplate = sidebarTemplates.find(tmpl => tmpl.id === selectedId)
  selectedTemplateRef.current = selectedTemplate

  const isOwnTemplate = selectedId === 'new' || selectedTemplate?.owner === authUserId
  const canEdit = isOwnTemplate || isAdvisorOrAdmin
  const canDelete =
    selectedId !== 'new' && (isOwnTemplate || persona === 'advisor' || persona === 'admin')

  const availableTraitIds = useMemo(() => {
    const existing = new Set(draftDetails.map(d => d.traitId))
    return allTraitIds.filter(id => !existing.has(id))
  }, [allTraitIds, draftDetails])

  useEffect(() => {
    if (open) setSelectedId(initialTemplateId ?? 'new')
  }, [open, initialTemplateId])

  useEffect(() => {
    if (!open) return
    if (selectedId === 'new') {
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      setNameError(null)
    } else if (selectedTemplate) {
      setDraftName(selectedTemplate.name)
      setDraftDetails(selectedTemplate.details)
      setIsDirty(false)
      setNameError(null)
      setOwnerScope(selectedTemplate.owner === farmerUserId ? 'farmer' : 'all')
    }
  }, [selectedId, selectedTemplate?.id, open])

  const requestNavigate = (targetId: string | 'new') => {
    if (isDirty) {
      setPendingNavigation(targetId)
      setShowUnsavedGuard(true)
    } else {
      setSelectedId(targetId)
    }
  }

  const requestClose = () => {
    if (isDirty) {
      setPendingNavigation(null)
      setShowUnsavedGuard(true)
    } else {
      onClose()
    }
  }

  const handleUnsavedDiscard = () => {
    setShowUnsavedGuard(false)
    setIsDirty(false)
    if (pendingNavigation !== null) {
      setSelectedId(pendingNavigation)
      setPendingNavigation(null)
    } else {
      onClose()
    }
  }

  const autosaveDetails = useCallback(
    async (newDetails: PracticeTemplateDetail[]) => {
      const currentSelectedId = selectedIdRef.current
      const trimmedName = draftNameRef.current.trim()
      if (!trimmedName) return

      if (currentSelectedId === 'new') {
        if (
          !newDetails.some(
            d => d.value !== undefined || d.input !== undefined || d.result !== undefined
          )
        )
          return
        if (isCreatingRef.current) return
        isCreatingRef.current = true
        try {
          const result = await createTemplate.mutateAsync({
            owner: templateOwnerRef.current,
            name: trimmedName,
            programId,
            taskId,
            details: newDetails,
          })
          setSelectedId(result.item.id)
        } catch {
          enqueueSnackbar(t('saveError'), { variant: 'error' })
        } finally {
          isCreatingRef.current = false
        }
        return
      }

      const saved = selectedTemplateRef.current
      if (trimmedName === (saved?.name ?? '') && equals(newDetails, saved?.details ?? [])) return
      try {
        await updateTemplate.mutateAsync({
          id: currentSelectedId,
          programId,
          taskId,
          owner: templateOwnerRef.current,
          name: trimmedName,
          details: newDetails,
        })
      } catch {
        enqueueSnackbar(t('saveError'), { variant: 'error' })
      }
    },
    [createTemplate, updateTemplate, programId, taskId, enqueueSnackbar, t]
  )

  const handleValueSave = useCallback(
    (
      traitId: string,
      year: number,
      filledValues: { value?: unknown; input?: unknown; result?: unknown }
    ) => {
      const value = filledValues?.value !== undefined ? { value: filledValues.value } : {}
      const input = filledValues?.input !== undefined ? { input: filledValues.input } : {}
      const result = filledValues?.result !== undefined ? { result: filledValues.result } : {}
      const prev = draftDetailsRef.current
      const idx = prev.findIndex(d => d.traitId === traitId && d.year === year)
      const newDetails =
        idx >= 0
          ? prev.map((d, i) => (i === idx ? { ...d, ...value, ...input, ...result } : d))
          : [...prev, { traitId, year, ...value, ...input, ...result }]
      setDraftDetails(newDetails)
      setIsDirty(true)
      autosaveDetails(newDetails)
    },
    [autosaveDetails]
  )

  const handleValueDelete = useCallback(
    (traitId: string, year: number) => {
      const newDetails = draftDetailsRef.current.filter(
        d => !(d.traitId === traitId && d.year === year)
      )
      setDraftDetails(newDetails)
      setIsDirty(true)
      autosaveDetails(newDetails)
    },
    [autosaveDetails]
  )

  const handleAddPractice = useCallback(
    (traitId: string) => {
      const newEntries = createEntriesForTrait(traitId)
      const newDetails = [...draftDetailsRef.current, ...newEntries]
      setDraftDetails(newDetails)
      setIsDirty(true)
      autosaveDetails(newDetails)
    },
    [createEntriesForTrait, autosaveDetails]
  )

  // Caller is responsible for computing the details from field data; this just sets+autosaves them
  const handlePrePopulate = useCallback(
    (newDetails: PracticeTemplateDetail[]) => {
      setDraftDetails(newDetails)
      setIsDirty(true)
      autosaveDetails(newDetails)
    },
    [autosaveDetails]
  )

  const confirmName = useCallback((): boolean => {
    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setNameError(t('nameRequiredError'))
      return false
    }
    const isDuplicate = sidebarTemplates.some(
      tmpl => tmpl.name === trimmedName && (selectedId === 'new' || tmpl.id !== selectedId)
    )
    if (isDuplicate) {
      setNameError(t('duplicateNameError'))
      return false
    }
    setNameError(null)
    return true
  }, [draftName, sidebarTemplates, selectedId, t])

  const handleSave = async (): Promise<boolean> => {
    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setIsDirty(true)
      setNameError(t('nameRequiredError'))
      return false
    }
    const isDuplicate = sidebarTemplates.some(
      tmpl => tmpl.name === trimmedName && (selectedId === 'new' || tmpl.id !== selectedId)
    )
    if (isDuplicate) {
      setNameError(t('duplicateNameError'))
      return false
    }
    setNameError(null)

    try {
      if (selectedId === 'new') {
        if (isCreatingRef.current) return false
        isCreatingRef.current = true
        try {
          const created = await createTemplate.mutateAsync({
            owner: templateOwner,
            name: trimmedName,
            programId,
            taskId,
            details: draftDetails,
          })
          setIsDirty(false)
          setSelectedId(created.item.id)
        } finally {
          isCreatingRef.current = false
        }
      } else {
        const saved = selectedTemplate
        if (trimmedName === (saved?.name ?? '') && equals(draftDetails, saved?.details ?? [])) {
          return true
        }
        await updateTemplate.mutateAsync({
          id: selectedId,
          programId,
          taskId,
          owner: templateOwner,
          name: trimmedName,
          details: draftDetails,
        })
        setIsDirty(false)
      }
      enqueueSnackbar(t('saveSuccess'), { variant: 'success' })
      return true
    } catch {
      enqueueSnackbar(t('saveError'), { variant: 'error' })
      return false
    }
  }

  const handleOwnerScopeChange = useCallback(
    async (scope: 'farmer' | 'all') => {
      setOwnerScope(scope)
      if (selectedIdRef.current === 'new') return
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newOwner = scope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!
      try {
        await updateTemplate.mutateAsync({
          id: selectedIdRef.current,
          programId,
          taskId,
          owner: newOwner,
          name: draftNameRef.current.trim(),
          details: draftDetailsRef.current,
        })
      } catch {
        enqueueSnackbar(t('saveError'), { variant: 'error' })
      }
    },
    [farmerUserId, authUserId, updateTemplate, programId, taskId, enqueueSnackbar, t]
  )

  const handleDeleteConfirm = async () => {
    if (selectedId === 'new') return
    try {
      await deleteTemplate.mutateAsync(selectedId)
      setShowDeleteConfirm(false)
      setSelectedId('new')
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      enqueueSnackbar(t('deleteSuccess'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('deleteError'), { variant: 'error' })
    }
  }

  return {
    // State
    selectedId,
    draftName,
    setDraftName,
    draftDetails,
    isDirty,
    setIsDirty,
    ownerScope,
    nameError,
    setNameError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showUnsavedGuard,
    setShowUnsavedGuard,
    // Derived
    sidebarTemplates,
    isLoadingTemplates,
    selectedTemplate,
    canEdit,
    canDelete,
    availableTraitIds,
    isAdvisorOrAdmin,
    isDeleting: deleteTemplate.isPending,
    authUserId,
    farmerUserId,
    // Handlers
    requestNavigate,
    requestClose,
    handleUnsavedDiscard,
    handleValueSave,
    handleValueDelete,
    handleAddPractice,
    handlePrePopulate,
    handleSave,
    confirmName,
    handleOwnerScopeChange,
    handleDeleteConfirm,
    isSaving: createTemplate.isPending || updateTemplate.isPending,
  }
}
